/**
 * WebView Detection Utility
 * Detects if the app is running inside a mobile WebView (Median, native iOS/Android app)
 * Used to optimize cold start performance
 */

/**
 * Check if running inside a WebView
 * Detects: Android WebView, iOS WKWebView, Median
 */
export function isWebView(): boolean {
  if (typeof navigator === 'undefined') return false;
  
  const ua = navigator.userAgent || '';
  
  // Android WebView detection
  // "wv" indicates Android WebView, also check for common WebView patterns
  const isAndroidWebView = /wv|Android.*Version\/\d/.test(ua) && /Android/.test(ua);
  
  // iOS WebView detection
  // iOS WebView has iPhone/iPad but NOT Safari in user agent
  const isiOSWebView = /(iPhone|iPad|iPod)/.test(ua) && !/Safari/.test(ua);
  
  // Median app detection (used in our mobile app)
  const isMedian = /Median/.test(ua);
  
  // Generic WebView markers
  const hasWebViewMarker = /WebView|wv/.test(ua);
  
  return isAndroidWebView || isiOSWebView || isMedian || hasWebViewMarker;
}

/**
 * Check if running in standard browser (not WebView)
 */
export function isStandardBrowser(): boolean {
  return !isWebView();
}

/**
 * Check if running in Android WebView specifically
 */
export function isAndroidWebView(): boolean {
  if (typeof navigator === 'undefined') return false;
  
  const ua = navigator.userAgent || '';
  return /Android/.test(ua) && isWebView();
}

/**
 * Get environment info for diagnostics
 */
export function getEnvironmentInfo(): {
  isWebView: boolean;
  userAgent: string;
  platform: string;
} {
  return {
    isWebView: isWebView(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    platform: typeof navigator !== 'undefined' ? navigator.platform : 'unknown',
  };
}
